import { LineSegment2D, Point2D } from '@paragon/geometry-library';
import { roundToIncrement } from '@paragon/utilities';

import { GeometricEcosystem } from '../../layout-geometry/ecosystem/GeometricEcosystem';
import { EntitySnapTarget } from '../EntitySnapTarget';
import { EntitySnapTargetSource } from '../EntitySnapTargetSource';
import { getAxisValue, getPerpendicularAxis } from './helpers';
import { InteractiveEditSnappingSettings } from './settings';
import { AxisPerpendicular, AxisRange, AxisSnapTarget, AxisSnapTargetKey, SNAP_AXES, SnapAxis } from './types';

const KIND_ENDPOINT = 'entity-snap-endpoint';
const KIND_MIDPOINT = 'entity-snap-midpoint';
const KIND_AXIS_LINE = 'entity-snap-axis-line';

type AxisSnapTargetKind = typeof KIND_ENDPOINT | typeof KIND_MIDPOINT | typeof KIND_AXIS_LINE;

const POSITION_HASH_GRID = 1 / 1024;

export interface PreComputedAxisSnapTargets {
  readonly x: readonly AxisSnapTarget[];
  readonly y: readonly AxisSnapTarget[];

  readonly byKey: ReadonlyMap<AxisSnapTargetKey, AxisSnapTarget>;
}

export function findAxisSnapTargetByKey(
  targets: PreComputedAxisSnapTargets,
  key: AxisSnapTargetKey,
): AxisSnapTarget | null {
  return targets.byKey.get(key) ?? null;
}

/**
 * Collects all axis snap targets available to the interactive editing snapping system. Targets are returned grouped by
 *   their axis, and also indexed by key for easy lookup.
 */
export function collectAxisSnapTargetsForInteractiveEdit(
  ecosystem: GeometricEcosystem,
  settings: InteractiveEditSnappingSettings,
  excludeOriginObjectGuids: ReadonlySet<string>,
): PreComputedAxisSnapTargets {
  const targetSource = new EntitySnapTargetSource(ecosystem);
  const filteredTargets = targetSource.getActiveTargetsForSelectionSettings(settings);

  const x: AxisSnapTarget[] = [];
  const y: AxisSnapTarget[] = [];
  const byKey = new Map<AxisSnapTargetKey, AxisSnapTarget>();

  function pushTarget(target: AxisSnapTarget): void {
    (target.axis === 'x' ? x : y).push(target);
    byKey.set(target.key, target);
  }

  for (const [filteredTargetIndex, target] of filteredTargets.entries()) {
    if (target.originObjectGuid != null && excludeOriginObjectGuids.has(target.originObjectGuid)) {
      continue;
    }

    const definition = target.definition;
    const boundingSegment = definition instanceof LineSegment2D ? definition : null;

    if (boundingSegment != null) {
      if (settings.entityAxisSnaps.endpoints) {
        for (const axis of SNAP_AXES) {
          pushTarget(
            makePointAxisTarget(target, filteredTargetIndex, boundingSegment.basePoint, axis, KIND_ENDPOINT, 0),
          );
          pushTarget(
            makePointAxisTarget(target, filteredTargetIndex, boundingSegment.endPoint, axis, KIND_ENDPOINT, 1),
          );
        }
      }

      if (settings.entityAxisSnaps.midpoints) {
        for (const axis of SNAP_AXES) {
          pushTarget(
            makePointAxisTarget(target, filteredTargetIndex, boundingSegment.midpoint, axis, KIND_MIDPOINT, null),
          );
        }
      }
    }

    if (settings.entityAxisSnaps.axisAlignedLines) {
      const nearestTarget = makeAxisAlignedLineTarget(target, filteredTargetIndex, boundingSegment);
      if (nearestTarget != null) {
        pushTarget(nearestTarget);
      }
    }
  }

  return { x, y, byKey };
}

function makePointAxisTarget(
  target: EntitySnapTarget,
  filteredTargetIndex: number,
  point: Point2D,
  axis: SnapAxis,
  kind: typeof KIND_ENDPOINT | typeof KIND_MIDPOINT,
  endpointIndex: 0 | 1 | null,
): AxisSnapTarget {
  const value = getAxisValue(point, axis);
  const perpendicularValue = getAxisValue(point, getPerpendicularAxis(axis));
  const pointHash = computePointHash(point);
  const keyBase = makeKeyBase(target, filteredTargetIndex, kind, axis);
  const key = (
    endpointIndex == null ? `${keyBase}:${pointHash}` : `${keyBase}:${pointHash}:${endpointIndex}`
  ) as AxisSnapTargetKey;

  return {
    axis,
    value,
    perpendicular: { kind: 'point', value: perpendicularValue },
    key,
  };
}

function makeAxisAlignedLineTarget(
  target: EntitySnapTarget,
  filteredTargetIndex: number,
  segment: LineSegment2D | null,
): AxisSnapTarget | null {
  const line = target.getDefiningLine();

  if (line.direction.isVertical()) {
    const range = segment ? makeAxisRange(segment.basePoint.y, segment.endPoint.y) : null;
    return makeLineAxisTarget(target, filteredTargetIndex, 'x', line.basePoint.x, range);
  }

  if (line.direction.isHorizontal()) {
    const range = segment ? makeAxisRange(segment.basePoint.x, segment.endPoint.x) : null;
    return makeLineAxisTarget(target, filteredTargetIndex, 'y', line.basePoint.y, range);
  }

  return null;
}

function makeLineAxisTarget(
  target: EntitySnapTarget,
  filteredTargetIndex: number,
  axis: SnapAxis,
  value: number,
  range: AxisRange | null,
): AxisSnapTarget {
  const perpendicular: AxisPerpendicular = range == null ? { kind: 'unbounded' } : { kind: 'range', range };
  const rangeHash = range == null ? '' : computeRangeHash(range);
  const key =
    `${makeKeyBase(target, filteredTargetIndex, KIND_AXIS_LINE, axis)}:${computeAxisValueHash(value)}:${rangeHash}` as AxisSnapTargetKey;

  return { axis, value, perpendicular, key };
}

function makeKeyBase(
  target: EntitySnapTarget,
  filteredTargetIndex: number,
  kind: AxisSnapTargetKind,
  axis: SnapAxis,
): string {
  return `${kind}:${filteredTargetIndex}:${target.type}:${target.originObjectGuid ?? ''}:${axis}`;
}

function makeAxisRange(a: number, b: number): AxisRange {
  return { lo: Math.min(a, b), hi: Math.max(a, b) };
}

function computePointHash(point: Point2D): string {
  return `${roundToIncrement(point.x, POSITION_HASH_GRID)}|${roundToIncrement(point.y, POSITION_HASH_GRID)}`;
}

function computeAxisValueHash(value: number): string {
  return `${roundToIncrement(value, POSITION_HASH_GRID)}`;
}

function computeRangeHash(range: AxisRange): string {
  return `${computeAxisValueHash(range.lo)}|${computeAxisValueHash(range.hi)}`;
}
